/* ═══════════════════════════════════════════════════════════════════════════
   HTTP transport
   ───────────────────────────────────────────────────────────────────────────
   The only place in the app that calls `fetch`. Everything above it — the
   forty-odd methods in `http.ts`, the hooks, the screens — is written against
   `HexApiError`, so this file's real job is translation: turn every way a
   network call can go wrong into one of the seven codes the UI already knows
   how to render.

   That translation is the whole reason the file exists. `fetch` fails in four
   quite different ways — a rejected promise for DNS and offline, a resolved
   promise with a 4xx for anything the server disliked, an abort that is not a
   failure at all, and a body that is not the JSON the status implied — and a
   component that has to know the difference is a component that will get it
   wrong. Below, all four become a thrown `HexApiError` or a typed value.

   Two things are handled here rather than by the caller because getting them
   wrong is silent:

   · Aborts stay aborts. React Query cancels a query on unmount and passes its
     own signal; if that surfaced as an error the user would see a failure
     toast for having navigated away. `AbortError` is rethrown untouched and
     never converted to a `HexApiError`.

   · Every request has a deadline. A fetch with no timeout is a spinner that
     spins until the tab closes — on a train, that is the entire experience of
     the app. The caller's signal and the timeout are combined, so whichever
     fires first wins and neither is lost.
   ═══════════════════════════════════════════════════════════════════════════ */

import { config } from '../config'
import { trackRequestFailure } from '../telemetry'
import { currentToken, renewSession } from '../session'
import { routes } from './routes'
import { HexApiError, type ApiError } from '../types'

export type Query = Record<string, string | number | boolean | undefined | null>

export interface RequestOptions {
  query?: Query
  /** Serialised as JSON when present, on every verb. `DELETE /devices` carries
   *  one deliberately: the push token is a credential for reaching somebody's
   *  phone, and a path segment lands in access logs and proxy caches. */
  body?: unknown
  signal?: AbortSignal
  /** Send the bearer token. Default true — the exceptions are the catalog
   *  reads and the sign-in calls, which have no session yet by definition. */
  auth?: boolean
  /** Internal. Cleared on the one retry that follows a token refresh, so a
   *  401 can never drive more than one refresh per call. */
  retrying?: boolean
  /** Makes a write safe to repeat. Sent as `Idempotency-Key`, never in the
   *  body — the server keys its stored response on the header, and a body
   *  field that disagreed would hand the payment provider a different key on
   *  the replay than on the original.
   *
   *  It belongs to the *intent*, not to the attempt: generated when the screen
   *  that will submit mounts, so the retry after a dropped connection carries
   *  the same key and replays rather than charging twice. */
  idempotencyKey?: string
}

/* ── error translation ────────────────────────────────────────────────────── */

/** HTTP status → the code the UI branches on.
 *
 *  Written as a mapping rather than a chain of ifs because the set is closed
 *  and the interesting cases are the ones that are easy to get wrong: 401 and
 *  403 are different to a server and identical to this app (both mean "you
 *  cannot do this, signing in again is the only move"), and 408/504 are
 *  timeouts that behave like a dropped connection rather than like a bug. */
function codeForStatus(status: number): ApiError['code'] {
  if (status === 401 || status === 403) return 'unauthorized'
  if (status === 404 || status === 410) return 'not-found'
  if (status === 409) return 'conflict'
  if (status === 408 || status === 504) return 'offline'
  if (status === 429) return 'rate-limited'
  if (status === 400 || status === 422) return 'validation'
  if (status >= 500) return 'server'
  return 'server'
}

/** Fallbacks for a server that returned a status and no usable body.
 *
 *  These are English and not translated on purpose: they are the last resort,
 *  and every path the user is expected to hit has a `message` from the server
 *  or a translated string at the call site. A key here would render as a
 *  dotted path when it failed to resolve, which is worse than a plain
 *  sentence. */
const FALLBACK: Record<ApiError['code'], string> = {
  'not-found': 'We could not find that',
  unauthorized: 'Please sign in to continue',
  validation: 'Please check the details and try again',
  conflict: 'That has already changed — reload and try again',
  offline: 'No connection',
  'rate-limited': 'Too many attempts. Please wait a moment',
  server: 'The service is temporarily unavailable',
}

/** Pull `{ code, message, field }` out of an error response when the server
 *  sent one, trusting only what it recognises. A server that returns HTML from
 *  a proxy — which is what a 502 usually is — must not end up with a tag soup
 *  string rendered into a toast. */
async function errorFromResponse(res: Response): Promise<HexApiError> {
  const status = codeForStatus(res.status)
  let message = FALLBACK[status]
  let field: string | undefined

  try {
    const body: unknown = await res.json()
    if (body && typeof body === 'object') {
      const shape = body as Partial<ApiError>
      if (typeof shape.message === 'string' && shape.message.trim() !== '') {
        message = shape.message
      }
      if (typeof shape.field === 'string') field = shape.field
      /* The server's own code wins where it names one we understand — it knows
         things the status does not, e.g. a 400 that is really a conflict. */
      if (typeof shape.code === 'string' && shape.code in FALLBACK) {
        return new HexApiError(shape.code as ApiError['code'], message, field)
      }
    }
  } catch {
    /* Not JSON. The status is all there is, and it is enough. */
  }

  return new HexApiError(status, message, field)
}

/* ── request ──────────────────────────────────────────────────────────────── */

function buildUrl(path: string, query?: Query): string {
  const base = `${config.apiUrl}${path}`
  if (!query) return base
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    /* Undefined and null are "not asked for", not "asked for as empty". The
       difference matters: `?area=` is a filter on the empty string and would
       return nothing, where omitting it returns everything. */
    if (value === undefined || value === null) continue
    params.set(key, String(value))
  }
  const qs = params.toString()
  return qs === '' ? base : `${base}?${qs}`
}

/** The caller's cancellation and our deadline, as one signal.
 *
 *  `AbortSignal.any` is in every browser the app targets (Chrome 116+, Safari
 *  17.4+ — the floor is already `:has()` and `color-mix()`), so there is no
 *  manual controller-plumbing fallback here. */
function withDeadline(signal: AbortSignal | undefined): AbortSignal {
  const timeout = AbortSignal.timeout(config.apiTimeoutMs)
  return signal ? AbortSignal.any([signal, timeout]) : timeout
}

/* ── keeping the session alive ────────────────────────────────────────────────
   The access token lasts fifteen minutes; the refresh cookie lasts far longer,
   is HttpOnly, and rotates every time it is spent. Without the exchange below
   the app signs itself out a quarter of an hour after sign-in — not with an
   error, just with a session that quietly stops working while a perfectly good
   credential sits unused in the cookie jar.

   Single-flight, because a screen that fires five queries on mount would
   otherwise spend the cookie five times concurrently. Rotation means only the
   first of those wins and the other four invalidate the token the first was
   issued — so the naive version does not merely waste requests, it signs the
   user out. One in-flight promise, shared by every caller waiting on it.

   This is safe without a lock only because the browser is single-threaded:
   nothing can interleave between the read and the assignment below. */
let refreshing: Promise<boolean> | null = null

/** The shape of a refreshed session on the wire. Same envelope as sign-in. */
interface WireRefresh {
  accessToken?: string
  expiresIn?: number
}

function refreshAccessToken(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const wire = await request<WireRefresh>('POST', routes.refresh(), {
        /* No bearer token: the credential is the cookie, and the token this is
           replacing is the expired one that caused the 401. Sending it would
           invite this very handler to recurse into the call meant to end the
           loop. `retrying` says the same thing structurally. */
        auth: false,
        retrying: true,
      })
      if (!wire.accessToken) return false
      renewSession(
        wire.accessToken,
        /* Computed from arrival rather than from any clock the server sent, so
           a device with the wrong time is signed out at roughly the right
           moment instead of immediately or never. */
        new Date(Date.now() + (wire.expiresIn ?? 900) * 1000).toISOString(),
      )
      return true
    } catch {
      /* Any failure means the session is over. The caller's original 401 is
         what gets reported; this one is noise on top of it. */
      return false
    } finally {
      refreshing = null
    }
  })()
  return refreshing
}

async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { query, body, signal, auth = true, retrying = false, idempotencyKey } = options

  /* The browser already knows. Asking first turns a five-second timeout into
     an instant, actionable message — and `navigator.onLine` is only
     trustworthy in this direction: false means definitely offline, true means
     "there is an interface up", which is why nothing else here relies on it. */
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    throw new HexApiError('offline', FALLBACK.offline)
  }

  const headers: Record<string, string> = { Accept: 'application/json' }

  /* The document's language, which `i18n/index.ts` keeps current. Read off the
     DOM rather than imported from i18next so this module stays free of the
     React tree and cannot form an import cycle with it. */
  const lang = typeof document !== 'undefined' ? document.documentElement.lang : ''
  if (lang) headers['Accept-Language'] = lang

  if (auth) {
    const token = currentToken()
    if (token) headers.Authorization = `Bearer ${token}`
  }

  if (body !== undefined) headers['Content-Type'] = 'application/json'

  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey

  let res: Response
  try {
    res = await fetch(buildUrl(path, query), {
      method,
      headers,
      /* The refresh token is an HttpOnly cookie the server sets, and the API is
         a different origin from the app in every deployment. Without this the
         browser neither stores it nor sends it back, which is not a visible
         failure: sign-in succeeds, and then the session simply dies at the
         access token's expiry with nothing able to renew it. */
      credentials: 'include',
      signal: withDeadline(signal),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })
  } catch (err) {
    /* A caller-driven abort is not a failure — it is a route change. Rethrown
       as-is so React Query treats it as a cancellation rather than surfacing a
       toast for something the user did on purpose. */
    if (err instanceof DOMException && err.name === 'AbortError' && signal?.aborted) {
      throw err
    }
    /* Everything else that rejects `fetch` is a transport failure: DNS, TLS,
       a refused connection, CORS, or our own deadline. The user cannot act on
       the distinction and the message for all of them is the same. */
    if (err instanceof DOMException && err.name === 'TimeoutError') {
      /* A timeout is reported, a dropped connection is not. The difference is
         who is at fault: a request that ran out of our own deadline is the
         server being slow, which is ours to fix, while a failed connection on
         a phone is usually a lift or a basement. Reporting the second would
         fill the Issues screen with the mobile network. */
      trackRequestFailure(method, path, 'timeout', 'request exceeded the client deadline')
      throw new HexApiError('offline', 'The service took too long to respond')
    }
    trackRequestFailure(method, path, 'network', 'fetch rejected')
    throw new HexApiError('offline', FALLBACK.offline)
  }

  /* An expired access token, on a call that carried one. Renew and go again —
     once. Retrying a write is safe here specifically because a 401 is refused
     at the door: the handler never ran, so there is no half-placed order to
     duplicate. That would not be true of a 500. */
  if (res.status === 401 && auth && !retrying) {
    if (await refreshAccessToken()) {
      return request<T>(method, path, { ...options, retrying: true })
    }
  }

  if (!res.ok) {
    const error = await errorFromResponse(res)
    /* Every non-OK response becomes a breadcrumb; only a 5xx becomes a report.
       `trackRequestFailure` draws that line — a 404 or a 409 is the server
       working correctly and belongs in the trail behind whatever the customer
       does next, not in a list of things to fix. */
    trackRequestFailure(method, path, res.status, error.message)
    throw error
  }

  /* 204, and any other body-less success. `Promise<void>` methods land here,
     and calling `.json()` on an empty body throws. */
  if (res.status === 204 || res.headers.get('Content-Length') === '0') {
    return undefined as T
  }

  try {
    return (await res.json()) as T
  } catch {
    /* A 200 whose body is not JSON is a broken deployment — a proxy error page
       served with the wrong status, usually. `server` rather than `validation`
       because nothing the user typed caused it. */
    throw new HexApiError('server', FALLBACK.server)
  }
}

/* ── verbs ────────────────────────────────────────────────────────────────── */

export const http = {
  get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, options),
  post: <T>(path: string, options?: RequestOptions) => request<T>('POST', path, options),
  put: <T>(path: string, options?: RequestOptions) => request<T>('PUT', path, options),
  patch: <T>(path: string, options?: RequestOptions) => request<T>('PATCH', path, options),
  delete: <T>(path: string, options?: RequestOptions) => request<T>('DELETE', path, options),
}
